import { useEffect, useRef, useState } from 'react';

import {
  creativesQueryError,
  creativesQueryInFlight,
  creativesQueryTimedOut,
} from '@web-platform/shared-banana-redux/src/actions/adverts';
import { useAdsCapabilityCommonVariables } from '@web-platform/shared-banana-redux/src/network-layer/hooks/useAdsCapabilityCommonVariables';
import { getCreativesStatus } from '@web-platform/shared-banana-redux/src/state/combinedResults';
import { INITIAL } from '@web-platform/shared-universal-constants/src/network';
import { getAdsCapabilityHomepageRequestTransform } from '@web-platform/shared-universal-data-access-frontend/src/network/requestTransforms/adsCapability';
import { useQuery } from '@web-platform/shared-universal-redux/src/network-layer/internals/queryApi';
import {
  getQuery,
  getTrustedFunnel,
  getUserDevice,
} from '@web-platform/shared-universal-redux/src/state';
import { ADS_CAPABILITY_HOMEPAGE } from '@web-platform/shared-universal-types/src/network/requestTypes';
import { buildHomepageAdsCapabilityVariables } from '@web-platform/shared-universal-utils-universal/src/adverts/homepageAdsCapabilityVariables';
import { useDispatch, useSelector } from 'react-redux';

import type { NextRequestAt } from '@web-platform/shared-universal-types/src/redux/actions/nextRequestAt';
import type { State } from '@web-platform/shared-universal-types/src/redux/banana/state/state-banana-types';
import type { QueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';

const ADS_CAPABILITY_HOMEPAGE_TIMEOUT = 600;

const getRequestOnlyOnce = (query?: QueryItem, enabled: boolean = true): NextRequestAt =>
  query?.requestCount || !enabled ? null : INITIAL;

const isMoreDataExpected = (query?: QueryItem) =>
  !query || query.inFlight || query.lastResponseAt === null;

export const useHomepageAdsCapabilityCreativeData = () => {
  const dispatch = useDispatch();
  const creativesStatus = useSelector(getCreativesStatus);
  const enabled = creativesStatus === 'loading';
  const commonVariables = useAdsCapabilityCommonVariables();
  const { viewGuid } = useSelector(getTrustedFunnel);
  const { isMobilePhone } = useSelector(getUserDevice);
  const variables = buildHomepageAdsCapabilityVariables({
    ...commonVariables,
    isMobilePhone,
    // Per-view key: viewGuid regenerates on every soft-nav to homepage (via HOMEPAGE_LOADED),
    // so the ARS query key rotates each view and the once-only dedup never blocks a re-fire.
    // Matches the removed useEFSCreativeData hook, which keyed its query on viewGuid.
    viewId: viewGuid ?? commonVariables.viewId,
  });

  const query = useSelector((state: State) => getQuery(state, ADS_CAPABILITY_HOMEPAGE, variables));

  useQuery({
    type: ADS_CAPABILITY_HOMEPAGE,
    variables,
    request: getAdsCapabilityHomepageRequestTransform(variables),
    nextRequestAt: getRequestOnlyOnce(query, enabled),
  });

  const [timedOut, setTimedOut] = useState(false);
  const moreDataExpectedRef = useRef(false);
  const moreDataExpected = isMoreDataExpected(query);
  moreDataExpectedRef.current = moreDataExpected;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      if (moreDataExpectedRef.current) {
        setTimedOut(true);
      }
    }, ADS_CAPABILITY_HOMEPAGE_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [enabled]);

  // Dispatch lifecycle actions describing the ARS client fallback request's progress
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (timedOut) {
      dispatch(creativesQueryTimedOut());
    } else if (moreDataExpected) {
      dispatch(creativesQueryInFlight());
    } else if (query?.consecutiveErrorCount) {
      dispatch(creativesQueryError());
    } else {
      // No error state needed here; the query completed successfully
    }
  }, [dispatch, enabled, timedOut, moreDataExpected, query?.consecutiveErrorCount]);
};
